import { QrMenuData } from '@/data/qrMenuService';

// pdfmake + gömülü fontlar ~2MB; sadece indirme anında yüklensin diye
// dinamik import kullanılır.
const getPdfMake = async () => {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts')
  ]);
  pdfMake.addVirtualFileSystem(vfs);
  return pdfMake;
};

const sanitizeFilename = (name: string) =>
  name
    .toLocaleLowerCase('tr-TR')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[ıİ]/g, 'i')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[üÜ]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const formatPrice = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(num);
};

/**
 * Masaya/duvara asılabilir A4 QR posteri: şube adı, QR kod ve menü linki.
 */
export const downloadQrPosterPdf = async (params: {
  companyName?: string;
  branchName: string;
  url: string;
}) => {
  const pdfMake = await getPdfMake();

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 80, 40, 60],
    content: [
      params.companyName
        ? {
            text: params.companyName.toLocaleUpperCase('tr-TR'),
            alignment: 'center',
            fontSize: 14,
            color: '#6b7280',
            characterSpacing: 1.5
          }
        : null,
      {
        text: params.branchName,
        alignment: 'center',
        fontSize: 28,
        bold: true,
        margin: [0, 8, 0, 30]
      },
      {
        qr: params.url,
        fit: 300,
        alignment: 'center',
        eccLevel: 'M',
        margin: [0, 0, 0, 30]
      },
      {
        text: 'Menümüzü görüntülemek için QR kodu okutun',
        alignment: 'center',
        fontSize: 16,
        margin: [0, 0, 0, 12]
      },
      {
        text: params.url,
        alignment: 'center',
        fontSize: 10,
        color: '#6b7280',
        link: params.url
      }
    ].filter(Boolean),
    defaultStyle: { font: 'Roboto' }
  };

  pdfMake
    .createPdf(docDefinition)
    .download(`qr-menu-${sanitizeFilename(params.branchName)}.pdf`);
};

/**
 * Şubenin tüm menüsünü (kategoriler + ürünler + fiyatlar) PDF olarak indirir.
 */
export const downloadMenuPdf = async (data: QrMenuData) => {
  const pdfMake = await getPdfMake();

  const categories = (data.categories || []).filter(
    (category) => category.products && category.products.length > 0
  );

  const categoryBlocks = categories.flatMap((category) => [
    {
      text: category.name,
      fontSize: 15,
      bold: true,
      color: '#b45309',
      margin: [0, 16, 0, 6]
    },
    {
      table: {
        widths: ['*', 'auto'],
        body: category.products.map((product) => [
          {
            stack: [
              { text: product.name, fontSize: 11, bold: true },
              product.description
                ? {
                    text: product.description,
                    fontSize: 9,
                    color: '#6b7280',
                    margin: [0, 1, 0, 0]
                  }
                : null
            ].filter(Boolean),
            margin: [0, 3, 0, 3]
          },
          {
            text: formatPrice(product.price ?? product.basePrice),
            fontSize: 11,
            bold: true,
            alignment: 'right',
            margin: [0, 3, 0, 3]
          }
        ])
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0,
        hLineColor: () => '#e5e7eb',
        paddingLeft: () => 0,
        paddingRight: () => 0
      }
    }
  ]);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 60],
    content: [
      {
        text: data.company?.name?.toLocaleUpperCase('tr-TR') || '',
        alignment: 'center',
        fontSize: 11,
        color: '#6b7280',
        characterSpacing: 1.2
      },
      {
        text: `${data.branch.name} – Menü`,
        alignment: 'center',
        fontSize: 20,
        bold: true,
        margin: [0, 4, 0, 8]
      },
      ...(categoryBlocks.length
        ? categoryBlocks
        : [
            {
              text: 'Menüde henüz ürün bulunmuyor.',
              alignment: 'center',
              color: '#6b7280',
              margin: [0, 24, 0, 0]
            }
          ])
    ],
    footer: (currentPage: number, pageCount: number) => ({
      text: `${data.branch.name} • ${currentPage}/${pageCount}`,
      alignment: 'center',
      fontSize: 8,
      color: '#9ca3af',
      margin: [0, 20, 0, 0]
    }),
    defaultStyle: { font: 'Roboto' }
  };

  pdfMake
    .createPdf(docDefinition)
    .download(`menu-${sanitizeFilename(data.branch.name)}.pdf`);
};
